
// AI Integration Manager
// Handles interaction with AI service connections in local storage

import { StoredAIIntegration, AIIntegration } from '@/types/banner';

/**
 * Check if an AI service is connected
 * @param serviceId The ID of the AI service
 * @returns boolean indicating if the service is connected
 */
export const isAIServiceConnected = (serviceId: string): boolean => {
  try {
    // In a real app, this would check a database or API
    // For now, we'll simulate with localStorage
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    return aiServices[serviceId]?.isConnected === true;
  } catch (error) {
    console.error('Error checking AI service connection:', error);
    return false;
  }
};

/**
 * Get the API key for an AI service
 * @param serviceId The ID of the AI service
 * @returns string containing the API key or null if not found
 */
export const getAIServiceApiKey = (serviceId: string): string | null => {
  try {
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    return aiServices[serviceId]?.apiKey || null;
  } catch (error) {
    console.error('Error retrieving AI service API key:', error);
    return null;
  }
};

/**
 * Connect an AI service
 * @param serviceId The ID of the AI service
 * @param apiKey The API key for the service
 */
export const connectAIService = (serviceId: string, apiKey: string): void => {
  try {
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    aiServices[serviceId] = {
      apiKey,
      isConnected: true,
      lastUsed: new Date().toISOString()
    };
    localStorage.setItem('ai_services', JSON.stringify(aiServices));
  } catch (error) {
    console.error('Error connecting AI service:', error);
  }
};

/**
 * Disconnect an AI service
 * @param serviceId The ID of the AI service
 */
export const disconnectAIService = (serviceId: string): void => {
  try {
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    if (aiServices[serviceId]) {
      aiServices[serviceId].isConnected = false;
      localStorage.setItem('ai_services', JSON.stringify(aiServices));
    }
  } catch (error) {
    console.error('Error disconnecting AI service:', error);
  }
};

/**
 * Get all connected AI services
 * @returns An object containing all connected AI services
 */
export const getAllConnectedAIServices = () => {
  try {
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    
    // Filter to only include connected services
    const connectedServices: Record<string, any> = {};
    
    Object.entries(aiServices).forEach(([id, service]: [string, any]) => {
      if (service.isConnected) {
        connectedServices[id] = service;
      }
    });
    
    return connectedServices;
  } catch (error) {
    console.error('Error retrieving connected AI services:', error);
    return {};
  }
};

/**
 * Save AI integration settings
 * @param integrations Array of AI integrations to save
 */
export const saveAIIntegrationSettings = (integrations: AIIntegration[]): void => {
  try {
    // Convert AIIntegration array to format for storage
    const storageFormat: Record<string, StoredAIIntegration> = {};
    
    integrations.forEach(integration => {
      storageFormat[integration.id] = {
        id: integration.id,
        apiKey: integration.apiKey || '',
        isConnected: integration.isConnected,
        lastUsed: integration.lastUsed ? integration.lastUsed.toISOString() : undefined
      };
    });
    
    localStorage.setItem('ai_services', JSON.stringify(storageFormat));
  } catch (error) {
    console.error('Error saving AI integration settings:', error);
  }
};

/**
 * Load AI integration settings
 * @returns Array of stored AI integrations
 */
export const loadAIIntegrationSettings = (): StoredAIIntegration[] => {
  try {
    const aiServices = JSON.parse(localStorage.getItem('ai_services') || '{}');
    
    // Convert to array of StoredAIIntegration
    return Object.values(aiServices).map((service: any): StoredAIIntegration => ({
      id: service.id,
      apiKey: service.apiKey,
      isConnected: service.isConnected,
      lastUsed: service.lastUsed
    }));
  } catch (error) {
    console.error('Error loading AI integration settings:', error);
    return [];
  }
};
